/**
 * Filtering and timeline construction for the Taiken calendar.
 *
 * Pure functions, so the scheduling rules can be tested without React.
 */

import { addDays, closureFor, datesBetween, dayTypeForDate, parseSlot, toISODate } from './dates.js';
import { localized } from './i18n.js';

/**
 * Does an event's weather rule allow it under the visitor's chosen plan?
 *
 * @param {'sunny'|'rainy'} plan    Selected plan.
 * @param {string}          weather Event weather rule.
 * @returns {boolean}
 */
export function weatherAllows(plan, weather) {
    if (weather === 'all_weather') {
        return true;
    }
    return plan === 'sunny' ? weather === 'sunny_only' : weather === 'rainy_only';
}

/**
 * Does the event run on this date?
 *
 * Specific dates, when present, fully override the day-type rule.
 *
 * @param {Object} event    Event record.
 * @param {Date}   date     Date to test.
 * @param {Object} holidays Map of date => holiday name.
 * @param {Object} [closure] Result of closureFor() for this date.
 * @returns {boolean}
 */
export function eventRunsOnDate(event, date, holidays, closure) {
    const iso = toISODate(date);
    if (!isActiveOn(event, iso)) {
        return false;
    }
    /*
     * A date typed into 個別開催日, or covered by an explicit 連続開催期間, is a
     * deliberate statement by whoever edited the entry — it wins over the
     * closure calendar. Leave the date out of the entry to hide it.
     */
    if (event.rangeStart && event.rangeEnd && iso >= event.rangeStart && iso <= event.rangeEnd) {
        return true;
    }
    if (Array.isArray(event.specificDates) && event.specificDates.length > 0) {
        return event.specificDates.includes(iso);
    }
    if (event.rangeStart && event.rangeEnd) {
        // A range was given and this date falls outside it.
        return false;
    }
    // An entry described only by a recurrence rule follows the closure calendar.
    if (closure && closure.closed) {
        return false;
    }
    if (!Array.isArray(event.dayTypes) || event.dayTypes.length === 0) {
        return false;
    }
    return event.dayTypes.includes(dayTypeForDate(date, holidays));
}

/**
 * Apply the search box and the category / audience filters.
 *
 * @param {Array}  events  All events.
 * @param {Object} filters { plan, category, target, search, locale }.
 * @returns {Array}
 */
export function filterEvents(events, filters) {
    const { plan, category, target, search, locale } = filters;
    const query = String(search || '').trim().toLowerCase();

    return events.filter((event) => {
        if (!weatherAllows(plan, event.weather)) {
            return false;
        }
        if (category !== 'all' && !event.categories.some((cat) => cat.slug === category)) {
            return false;
        }
        if (target !== 'all' && !(event.targets || []).includes(target)) {
            return false;
        }
        if (query) {
            const haystack = [
                localized(locale, event.title, event.titleEn),
                localized(locale, event.summary, event.summaryEn),
                ...event.categories.map((cat) => localized(locale, cat.label, cat.labelEn)),
            ]
                .join(' ')
                .toLowerCase();
            if (!haystack.includes(query)) {
                return false;
            }
        }
        return true;
    });
}

/**
 * Is the event within its published run? Empty bounds mean "always".
 *
 * @param {Object} event Event record.
 * @param {string} iso   YYYY-MM-DD to test.
 * @returns {boolean}
 */
export function isActiveOn(event, iso) {
    if (event.validFrom && iso < event.validFrom) {
        return false;
    }
    if (event.validTo && iso > event.validTo) {
        return false;
    }
    return true;
}

/**
 * Time slots for a given date.
 *
 * Some experiences run to a different timetable on weekdays — 無限の鏡の間 is
 * 11:00-11:30 midweek but 11:00-12:00 at weekends. The weekday list is used
 * only when it has been filled in.
 *
 * @param {Object} event    Event record.
 * @param {Date}   date     Date being shown.
 * @param {Object} holidays Map of date => holiday name.
 * @returns {Array} Slot strings.
 */
export function slotsForDate(event, date, holidays) {
    const weekdaySlots = event.timeSlotsWeekday || [];
    if (weekdaySlots.length > 0 && dayTypeForDate(date, holidays) === 'weekday') {
        return weekdaySlots;
    }
    return event.timeSlots || [];
}

/**
 * Capacity text for a given date, honouring the optional weekday variant.
 *
 * @param {Object} event    Event record.
 * @param {Date}   date     Date being shown.
 * @param {Object} holidays Map of date => holiday name.
 * @returns {string}
 */
export function capacityForDate(event, date, holidays) {
    if (event.capacityWeekday && dayTypeForDate(date, holidays) === 'weekday') {
        return event.capacityWeekday;
    }
    return event.capacity || '';
}

/**
 * Build the hour-by-hour timeline for one day.
 *
 * The hour range is derived from the events actually present, widened to the
 * park's opening hours — so a slot outside the usual window is still shown
 * rather than silently dropped. Events spanning more than one hour appear as a
 * full card in their starting hour and as a compact continuation in each later
 * hour they cover.
 *
 * Events with no valid time slot are returned separately as all-day entries.
 *
 * @param {Array}  events    Events already filtered to this date.
 * @param {Object} openHours { start, end } fallback window.
 * @param {Date}   date      The day being shown, for weekday slot variants.
 * @param {Object} holidays  Map of date => holiday name.
 * @returns {{hours:Array, allDay:Array, isEmpty:boolean}}
 */
export function buildTimeline(events, openHours, date, holidays) {
    const entries = [];
    const allDay = [];

    events.forEach((event) => {
        const parsed = slotsForDate(event, date, holidays).map(parseSlot).filter(Boolean);

        if (parsed.length === 0) {
            allDay.push(event);
            return;
        }
        parsed.forEach((slot) => entries.push({ event, slot }));
    });

    if (entries.length === 0) {
        return { hours: [], allDay, isEmpty: allDay.length === 0 };
    }

    const startHours = entries.map((entry) => entry.slot.startHour);
    const lastHours = entries.map((entry) => entry.slot.lastHour);
    const from = Math.min(openHours.start, ...startHours);
    const to = Math.max(openHours.end - 1, ...lastHours);

    const hours = [];
    for (let hour = from; hour <= to; hour += 1) {
        hours.push({
            hour,
            starting: entries.filter((entry) => entry.slot.startHour === hour),
            ongoing: entries.filter(
                (entry) => entry.slot.startHour < hour && entry.slot.lastHour >= hour
            ),
        });
    }

    return { hours, allDay, isEmpty: false };
}

/**
 * Describe a value that varies by day type, for contexts not tied to one date
 * (the details dialog and the list table). Where an event runs to the same
 * timetable all week the plain value is returned unchanged.
 *
 * @param {string|Array} weekdayValue Weekday variant, empty when there is none.
 * @param {string|Array} defaultValue Value used at weekends and holidays.
 * @param {Function}     t            Translator.
 * @param {Function}     [format]     Optional formatter for each side.
 * @returns {string}
 */
export function describeByDayType(weekdayValue, defaultValue, t, format) {
    const render = format || ((value) => String(value || ''));
    const weekend = render(defaultValue);
    const isEmpty = (value) => !value || (Array.isArray(value) && value.length === 0);

    if (isEmpty(weekdayValue)) {
        return weekend;
    }
    const weekday = render(weekdayValue);
    if (weekday === weekend) {
        return weekend;
    }
    return t('byDayType', { weekday, weekend });
}

/**
 * The continuous runs an event has — an explicit range, plus any stretch of
 * consecutive individual dates. A single isolated date is not a run.
 *
 * @param {Object} event Event record.
 * @returns {Array} [{ start, end }] in date order.
 */
export function multiDayRuns(event) {
    const runs = [];

    if (event.rangeStart && event.rangeEnd && event.rangeEnd > event.rangeStart) {
        runs.push({ start: event.rangeStart, end: event.rangeEnd });
    }

    const dates = [...(event.specificDates || [])].sort();
    let i = 0;
    while (i < dates.length) {
        let j = i;
        while (j + 1 < dates.length && addDays(dates[j], 1) === dates[j + 1]) {
            j += 1;
        }
        if (j > i) {
            runs.push({ start: dates[i], end: dates[j] });
        }
        i = j + 1;
    }

    return runs.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
}

/**
 * Does this event span more than one day anywhere?
 *
 * @param {Object} event Event record.
 * @returns {boolean}
 */
export function isMultiDay(event) {
    return multiDayRuns(event).length > 0;
}

/**
 * Pack multi-day runs into horizontal lanes for the month grid, so a run keeps
 * the same vertical position across every day it covers and reads as one band.
 *
 * Runs that do not overlap share a lane.
 *
 * @param {Array}  events    Events to lay out.
 * @param {string} fromDate  First date shown, YYYY-MM-DD.
 * @param {string} toDate    Last date shown, YYYY-MM-DD.
 * @returns {Array} [{ event, start, end, lane }] sorted by lane then start.
 */
export function assignRunLanes(events, fromDate, toDate) {
    const segments = [];

    events.forEach((event) => {
        multiDayRuns(event).forEach((run) => {
            // Clip to the window being drawn.
            const start = run.start < fromDate ? fromDate : run.start;
            const end = run.end > toDate ? toDate : run.end;
            if (start <= end && run.start <= toDate && run.end >= fromDate) {
                segments.push({ event, start, end, trueStart: run.start, trueEnd: run.end });
            }
        });
    });

    segments.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

    const laneEnds = [];
    segments.forEach((segment) => {
        let lane = laneEnds.findIndex((end) => end < segment.start);
        if (lane === -1) {
            lane = laneEnds.length;
        }
        laneEnds[lane] = segment.end;
        segment.lane = lane;
    });

    return segments;
}

/**
 * Dates an event actually runs on, for listing in the details dialog.
 *
 * Returns null for an event described only by a recurrence rule — the "days"
 * row already says "weekends and holidays" and listing every Saturday helps
 * nobody.
 *
 * @param {Object} event Event record.
 * @returns {Array|null} Date strings, or null when there is nothing to list.
 */
export function occurrenceDates(event) {
    if (event.rangeStart && event.rangeEnd) {
        return datesBetween(event.rangeStart, event.rangeEnd);
    }
    const dates = [...(event.specificDates || [])].sort();
    return dates.length > 0 ? dates : null;
}

/**
 * Distinct category records across a set of events, for the filter chips.
 *
 * @param {Array} events All events.
 * @returns {Array} Category records, de-duplicated by slug.
 */
export function collectCategories(events) {
    const seen = new Map();
    events.forEach((event) => {
        (event.categories || []).forEach((cat) => {
            if (!seen.has(cat.slug)) {
                seen.set(cat.slug, cat);
            }
        });
    });
    return Array.from(seen.values());
}

/**
 * Human-readable summary of when an event runs.
 *
 * @param {Object} event Event record.
 * @returns {string} A translation key for the day-type description.
 */
export function dayTypeKey(event) {
    if (Array.isArray(event.specificDates) && event.specificDates.length > 0) {
        return 'days_specific';
    }
    const types = event.dayTypes || [];
    const weekday = types.includes('weekday');
    const weekend = types.includes('weekend_holiday');
    if (weekday && weekend) {
        return 'days_both';
    }
    if (weekend) {
        return 'days_weekend_holiday';
    }
    return 'days_weekday';
}
